import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Modal, Skeleton, useToast } from '../../../design-system';
import { money, annualValue, basisLabel, FREQUENCY_LABEL, ordinal } from '../../../lib/retainer';
import '../../styles/Retainers.css';

// One arrangement in full: what it covers, what was agreed, the agreement paper,
// and every invoice it has produced.
//
// ⚠️ THE INVOICES ARE READ OFF THE INVOICE LINES, never off a counter on the
// retainer — the same rule that makes "which charges are billed" an
// invoice-line question in utils/finance.js. A count stored on the agreement
// would drift the first time an invoice was cancelled.

const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—');

function RetainerDrawer({ retainerId, onClose, onEdit, onChanged }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return apiClient.get(`/api/retainers/${retainerId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.danger(e.response?.data?.error || 'Could not load that retainer'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retainerId]);

  useEffect(() => { load(); }, [load]);

  /**
   * Open the agreement in a new tab.
   *
   * ⚠️ FETCHED AS A BLOB, NOT LINKED TO DIRECTLY. The preview route is behind
   * the session's Authorization header, and a plain window.open carries none —
   * it would land on a 401 page. The object URL is revoked on a timer rather
   * than immediately because Chrome needs it to survive the tab's own load.
   */
  const preview = async () => {
    try {
      const r = await apiClient.get(`/api/retainers/${retainerId}/agreement`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/html' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not render the agreement');
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await apiClient.post(`/api/retainers/${retainerId}/agreement`);
      const { status, reason } = r.data;
      if (status === 'filed') toast.success("Agreement filed in the client's documents");
      else if (status === 'superseded') toast.success(`Agreement updated to v${r.data.retainer.agreementVersion}`);
      else if (status === 'skipped') toast.info(`Not filed — ${reason}`);
      else toast.danger(`Could not file the agreement — ${reason || 'unknown error'}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not generate the agreement');
    } finally {
      setGenerating(false);
    }
  };

  const r = data?.retainer;
  const per = r ? FREQUENCY_LABEL[r.frequency].toLowerCase() : '';

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={r ? `${r.agreementNumber || 'Retainer'} — ${r.clientName}` : 'Retainer'}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {r && <Button variant="secondary" onClick={preview}>Preview agreement</Button>}
          {r && (
            <Button variant="secondary" onClick={generate} loading={generating}>
              {r.agreementGeneratedAt ? 'Regenerate & file' : 'Generate & file'}
            </Button>
          )}
          {r && <Button variant="primary" onClick={() => onEdit?.(r)}>Edit</Button>}
        </>
      )}
    >
      {loading || !r ? <Skeleton.Text lines={8} /> : (
        <div className="ret-form">
          <div className="ret-detail-head">
            <div>
              <div className="ret-detail-title">{r.title}</div>
              <div className="ret-muted">
                {FREQUENCY_LABEL[r.frequency]} · from {fmtDate(r.startDate)}
                {r.endDate ? ` to ${fmtDate(r.endDate)}` : ', open-ended'}
              </div>
              {/* The billing day and what it means next. `nextBillsOn` is the
                  SERVER's answer (utils/retainer.js#nextBillingFor mirrors the
                  generator) — not worked out here, or the screen could promise
                  a date the cron does not act on. */}
              <div className="ret-muted">
                Invoiced on the {ordinal(r.billingDay || 1)} of the month
                {r.nextBillsOn && (
                  r.nextIsOverdue
                    ? <span className="ret-overdue"> · {r.nextPeriod} is due now</span>
                    : <span> · next {fmtDate(r.nextBillsOn)} for {r.nextPeriod}</span>
                )}
              </div>
            </div>
            <span className={`ret-pill ${r.isActive ? 'ret-s-active' : 'ret-s-stopped'}`}>
              {r.isActive ? 'Active' : 'Stopped'}
            </span>
          </div>

          {/* ── Services ── */}
          <div className="ret-section">
            <h4 className="ret-h">Services covered</h4>
            {r.services.length === 0 ? (
              <p className="ret-empty">A flat fee with no services listed.</p>
            ) : (
              <table className="ret-table ret-table-tight">
                <thead>
                  <tr><th>Service</th><th>Head</th><th className="ret-num">Fee</th></tr>
                </thead>
                <tbody>
                  {r.services.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <span className="ret-name">{s.name}</span>
                        <div className="ret-muted">
                          {s.groupName}
                          {s.description ? ` · ${s.description}` : ''}
                          {s.listAmount != null && s.listAmount !== s.amount
                            ? ` · standard ${money(s.listAmount)} ${basisLabel(s.billingBasis)}`
                            : ''}
                        </div>
                      </td>
                      <td className="ret-muted">{s.kind === 'ope' ? 'Reimbursement' : 'Professional fees'}</td>
                      <td className="ret-num">{money(s.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── The fee ── */}
          <div className="ret-totals">
            {r.discount !== 0 && (
              <>
                <div className="ret-total-row">
                  <span>Services listed</span><b>{money(r.listTotal)}</b>
                </div>
                <div className={`ret-total-row ${r.discount > 0 ? 'ret-disc' : 'ret-prem'}`}>
                  <span>{r.discount > 0 ? 'Agreed concession' : 'Agreed premium'}</span>
                  <b>{r.discount > 0 ? '−' : '+'}{money(Math.abs(r.discount))}</b>
                </div>
              </>
            )}
            <div className="ret-total-row ret-final">
              <span>Bills {per.replace('every ', 'every ')}</span><b>{money(r.amount)}</b>
            </div>
            <div className="ret-total-row ret-muted">
              <span>Over a full year</span><span>{money(annualValue(r.amount, r.frequency))}</span>
            </div>
          </div>

          {r.scope && (
            <div className="ret-section">
              <h4 className="ret-h">Scope</h4>
              <p className="ret-pre">{r.scope}</p>
            </div>
          )}

          {/* ── The paper ── */}
          <div className="ret-section">
            <h4 className="ret-h">Agreement</h4>
            {data.agreementDocument ? (
              <p className="ret-sub">
                <b>{data.agreementDocument.fileName}</b> — version {data.agreementDocument.version},
                filed {fmtDate(data.agreementDocument.createdAt)} in the client&apos;s documents.
                {' '}
                {data.agreementDocument.sharedWithClient
                  ? 'Shared with the client.'
                  : 'Not shared with the client — share it from Documents when it is ready to go out.'}
              </p>
            ) : (
              <p className="ret-sub">
                No agreement generated yet. Generating files a copy into this client&apos;s documents under
                Agreements &amp; Legal; regenerating supersedes it rather than replacing it, so the earlier
                terms stay on record.
              </p>
            )}
          </div>

          {/* ── What it has billed ── */}
          <div className="ret-section">
            <h4 className="ret-h">Invoices raised</h4>
            {data.invoices.length === 0 ? (
              <p className="ret-empty">
                Nothing billed yet. The first invoice is generated once the first period has ended.
              </p>
            ) : (
              <table className="ret-table ret-table-tight">
                <thead>
                  <tr><th>Number</th><th>Period</th><th>Date</th><th className="ret-num">Total</th><th className="ret-num">Due</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {data.invoices.map((i) => (
                    <tr key={i.id}>
                      <td className="ret-name">{i.number}</td>
                      <td className="ret-muted">{i.periods.join(', ') || '—'}</td>
                      <td className="ret-muted">{fmtDate(i.invoiceDate)}</td>
                      <td className="ret-num">{money(i.total)}</td>
                      <td className="ret-num">{money(i.amountDue)}</td>
                      <td className="ret-muted">{i.status.replace(/_/g, ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

export default RetainerDrawer;
